import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import type { Comparison, EmployeeComparison, CompensationLine, Line } from '../types';

interface Props {
  employee: Line;
  comparison: Comparison | null;
  onClose: () => void;
}

const tokens = {
  ink: '#12262B',
  card: '#FFFFFF',
  page: '#F4F6F8',
  line: '#E7EAF0',
  muted: '#66748B',
};

function fmtDateLocal(iso: string | undefined): string {
  if (!iso) return '';
  const parts = String(iso).split('-');
  return parts[2] + '/' + parts[1] + '/' + parts[0];
}

function fmtM(s: number | string | null | undefined): string | null {
  if (s === null || s === undefined) return null;
  const n = Number(s);
  if (isNaN(n)) return null;
  return '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtH(h: number | string | null | undefined): string | null {
  if (h === null || h === undefined) return null;
  const n = Number(h);
  if (isNaN(n)) return null;
  const s = n.toFixed(2);
  return s.replace(/\.?0+$/, '');
}

function cell(v: React.ReactNode): React.ReactNode {
  return v == null ? <span style={{ color: tokens.muted }}>-</span> : v;
}

function findLine(lines: CompensationLine[], label: string): CompensationLine | null {
  for (let i = 0; i < lines.length; i++) if (lines[i].label === label) return lines[i];
  return null;
}

const thStyle: React.CSSProperties = { padding: '12px 14px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: tokens.ink, borderBottom: '1.5px solid ' + tokens.ink, verticalAlign: 'top' };
const thLStyle: React.CSSProperties = { padding: '12px 14px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: tokens.ink, borderBottom: '1.5px solid ' + tokens.ink };
const tdMoney: React.CSSProperties = { padding: '8px 14px', textAlign: 'right', fontSize: 13, color: tokens.ink, fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid ' + tokens.line };
const tdLabel: React.CSSProperties = { padding: '8px 14px', textAlign: 'left', fontSize: 13, color: tokens.ink, borderBottom: '1px solid ' + tokens.line };
const sectionRow: React.CSSProperties = { background: tokens.page };
const sectionCell: React.CSSProperties = { padding: '10px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: tokens.ink, borderBottom: '1px solid ' + tokens.line };
const summaryLabel: React.CSSProperties = { ...tdLabel, fontWeight: 700, paddingLeft: 30 };
const summaryVal: React.CSSProperties = { ...tdMoney, fontWeight: 700 };

export default function CompareModal({ employee, comparison, onClose }: Props) {
  useEffect(() => {
    const h = (ev: KeyboardEvent): void => { if (ev.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const stubId = employee && employee.stub_id;
  if (!stubId || !comparison || !comparison.employees) return null;
  const data: EmployeeComparison | undefined = comparison.employees[String(stubId)];
  if (!data || !data.has_comparison) return null;

  const employeeName = data.employee_name || 'Employee';
  const priorDate = comparison.prior_pay_date;
  const currentDate = comparison.current_pay_date;

  const priorLines: CompensationLine[] = (data.prior && data.prior.compensation_lines) || [];
  const currentLines: CompensationLine[] = (data.current && data.current.compensation_lines) || [];
  const lineLabels: string[] = [];
  priorLines.forEach((l) => { if (lineLabels.indexOf(l.label) < 0) lineLabels.push(l.label); });
  currentLines.forEach((l) => { if (lineLabels.indexOf(l.label) < 0) lineLabels.push(l.label); });

  const priorNet = data.prior?.net_pay ?? null;
  const currentNet = data.current?.net_pay ?? null;
  const priorTaxes = data.prior?.taxes_and_deductions ?? null;
  const currentTaxes = data.current?.taxes_and_deductions ?? null;
  const priorTotal = data.prior?.total_pay ?? null;
  const currentTotal = data.current?.total_pay ?? null;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(14, 26, 26, 0.45)', zIndex: 1300 }}
      />
      <div
        role="dialog"
        aria-label="Compare to last regular payday"
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: tokens.card, borderRadius: 14,
          maxWidth: 720, width: 'calc(100% - 48px)', maxHeight: 'calc(100vh - 96px)',
          overflowY: 'auto', padding: 32, zIndex: 1301,
          boxShadow: '0 12px 40px rgba(18, 38, 43, 0.18)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 600, color: tokens.ink }}>Compare to last regular payday</div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: tokens.muted, padding: 4, borderRadius: 6, display: 'inline-flex',
            }}
          >
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid ' + tokens.line }}>
          <thead>
            <tr>
              <th style={thLStyle}>Employee: {employeeName}</th>
              <th style={thStyle}>
                <div>Last Payday</div>
                <div style={{ fontSize: 11, color: tokens.muted, fontWeight: 500, marginTop: 2 }}>({fmtDateLocal(priorDate)})</div>
              </th>
              <th style={thStyle}>
                <div>Current Payday</div>
                <div style={{ fontSize: 11, color: tokens.muted, fontWeight: 500, marginTop: 2 }}>({fmtDateLocal(currentDate)})</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr style={sectionRow}>
              <td colSpan={3} style={sectionCell}>COMPENSATION</td>
            </tr>
            {lineLabels.map((label) => {
              const pl = findLine(priorLines, label);
              const cl = findLine(currentLines, label);
              const priorAmt = pl ? fmtM(pl.amount) : null;
              const curAmt = cl ? fmtM(cl.amount) : null;
              const priorRate = pl && pl.rate ? fmtM(pl.rate) : null;
              const curRate = cl && cl.rate ? fmtM(cl.rate) : null;
              const priorHours = pl && pl.hours ? fmtH(pl.hours) : null;
              const curHours = cl && cl.hours ? fmtH(cl.hours) : null;
              const showSubRows = Boolean(priorRate || curRate || priorHours || curHours);
              return (
                <React.Fragment key={label}>
                  <tr>
                    <td style={{ ...tdLabel, paddingLeft: 30 }}>{label}</td>
                    <td style={tdMoney}>{cell(priorAmt)}</td>
                    <td style={tdMoney}>{cell(curAmt)}</td>
                  </tr>
                  {showSubRows && (
                    <>
                      <tr>
                        <td style={{ ...tdLabel, paddingLeft: 46, color: tokens.muted, fontSize: 12 }}>Rate</td>
                        <td style={{ ...tdMoney, color: tokens.muted, fontSize: 12 }}>{cell(priorRate ? priorRate + '/hour' : null)}</td>
                        <td style={{ ...tdMoney, color: tokens.muted, fontSize: 12 }}>{cell(curRate ? curRate + '/hour' : null)}</td>
                      </tr>
                      <tr>
                        <td style={{ ...tdLabel, paddingLeft: 46, color: tokens.muted, fontSize: 12 }}>Hours</td>
                        <td style={{ ...tdMoney, color: tokens.muted, fontSize: 12 }}>{cell(priorHours)}</td>
                        <td style={{ ...tdMoney, color: tokens.muted, fontSize: 12 }}>{cell(curHours)}</td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              );
            })}

            <tr style={sectionRow}>
              <td colSpan={3} style={sectionCell}>TAXES</td>
            </tr>
            <tr>
              <td style={{ ...tdLabel, paddingLeft: 30 }}>Income Tax</td>
              <td style={tdMoney}>{cell(data.prior ? fmtM(data.prior.income_tax) : null)}</td>
              <td style={tdMoney}>{cell(data.current ? fmtM(data.current.income_tax) : null)}</td>
            </tr>
            <tr>
              <td style={{ ...tdLabel, paddingLeft: 30 }}>Employment Insurance</td>
              <td style={tdMoney}>{cell(data.prior ? fmtM(data.prior.ei) : null)}</td>
              <td style={tdMoney}>{cell(data.current ? fmtM(data.current.ei) : null)}</td>
            </tr>
            <tr>
              <td style={{ ...tdLabel, paddingLeft: 30 }}>Canada Pension Plan</td>
              <td style={tdMoney}>{cell(data.prior ? fmtM(data.prior.cpp) : null)}</td>
              <td style={tdMoney}>{cell(data.current ? fmtM(data.current.cpp) : null)}</td>
            </tr>
            <tr>
              <td style={{ ...tdLabel, paddingLeft: 30 }}>Second Canada Pension Plan</td>
              <td style={tdMoney}>{cell(data.prior ? fmtM(data.prior.cpp2) : null)}</td>
              <td style={tdMoney}>{cell(data.current ? fmtM(data.current.cpp2) : null)}</td>
            </tr>

            <tr>
              <td style={summaryLabel}>Total pay</td>
              <td style={summaryVal}>{cell(fmtM(priorTotal))}</td>
              <td style={summaryVal}>{cell(fmtM(currentTotal))}</td>
            </tr>
            <tr>
              <td style={summaryLabel}>Taxes and deductions</td>
              <td style={summaryVal}>{cell(fmtM(priorTaxes))}</td>
              <td style={summaryVal}>{cell(fmtM(currentTaxes))}</td>
            </tr>
            <tr>
              <td style={summaryLabel}>Net pay</td>
              <td style={summaryVal}>{cell(fmtM(priorNet))}</td>
              <td style={summaryVal}>{cell(fmtM(currentNet))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}